import { Boss, type BossDef } from './boss-base';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { Horizon } from './singularity-horizon';

/**
 * Singularity — a black hole with a CONSTANT gravity well that
 * steadily draws the ship inward (never violent — you can always out-thrust
 * it). Its bullets spiral out like an accretion disk, so the safe lanes
 * rotate around it. Force via the shared environmentalForce hook. Its EVENT
 * HORIZON (wave 4) pulls player shots into orbit and swallows them, then
 * opens a clear window on the bare core (singularity-horizon.ts,
 * BOSS_MECHANICS.singularity). docs/02.
 * Signatures: gravity well + event horizon (always), accretion spiral (every phase).
 */
export const SINGULARITY: BossDef = {
  id: 'singularity',
  nameKey: 'boss.singularity',
  tier: 4,
  minRoom: 3,
  baseHp: 1150, // the horizon costs uptime — a lighter hull keeps TTK
  radius: 50,
  bodyKey: 'boss/singularity/body',
  movement: 'hover',
  contactDamage: 2,
  phases: [
    {
      hpPct: 1.0,
      patterns: [
        {
          // Accretion: the arms curl as they leave the horizon (B3 curve)
          type: 'spiral', bullet: 'bullet/enemy/void', arms: 2, interval: 0.16,
          angularSpeed: 60, speed: 120, radius: 9, damage: 1, motion: { curveDeg: 20 },
        },
      ],
    },
    {
      hpPct: 0.6,
      patterns: [
        {
          type: 'spiral', bullet: 'bullet/enemy/void', arms: 3, interval: 0.14,
          angularSpeed: 75, speed: 130, radius: 9, damage: 1,
        },
        {
          type: 'radial', bullet: 'bullet/enemy/ring', count: 12, interval: 3.4,
          spinDeg: 10, speed: 100, radius: 10, damage: 2, unlockTier: 1,
        },
      ],
    },
    {
      hpPct: 0.3,
      speedMul: 1.2,
      patterns: [
        {
          type: 'spiral', bullet: 'bullet/enemy/void', arms: 4, interval: 0.12,
          angularSpeed: 90, speed: 140, radius: 9, damage: 1,
        },
      ],
    },
  ],
  evolve: [
    {
      // Event ring: a slow heavy ring rolls off the horizon
      tier: 2,
      pattern: {
        type: 'radial', bullet: 'bullet/enemy/ring', count: 8, interval: 4.6,
        spinDeg: 22, speed: 95, radius: 10, damage: 2,
      },
    },
    {
      // Jet: a telegraphed void fan along the polar axis
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/void', count: 3, spreadDeg: 16,
        interval: 3.6, telegraph: 0.5, speed: 250, radius: 10, damage: 1,
      },
    },
  ],
};

const PULL = 130; // px/s near the rim — constant, gentle
const RANGE = 900;
const ENRAGE_PULL = 185;

export class SingularityBoss extends Boss {
  private readonly horizon = new Horizon();

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(SINGULARITY, level, x, y, homeX, homeY);
    this.allowReinforce = false; // the horizon is its gate
    this.view.addChildAt(this.horizon.back, 0); // the disk lies behind the body
    this.view.addChild(this.horizon.front); // the open window shows over it
  }

  /** Only the open window exposes the core (auto-aim reads this too). */
  override get isVulnerable(): boolean {
    return super.isVulnerable && this.horizon.open;
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    super.update(dt, px, py, bullets, telegraphs);
    if (this.dead) return;
    this.horizon.update(dt, this.x, this.y, bullets);
  }

  /** Closed: shots that spiral into the core are swallowed — no damage, no number. */
  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    if (this.horizon.open) return super.absorbBullets(bullets);
    const eaten = bullets.collideCircle(true, this.x, this.y, this.def.radius, () => 0, false);
    if (eaten > 0) this.horizon.swallowed();
    return { hits: 0, dealt: 0 };
  }

  /** Blasts vanish into the closed horizon too. */
  override takeDamage(amount: number): boolean {
    if (!this.horizon.open) return false;
    return super.takeDamage(amount);
  }

  override render(alpha: number): void {
    super.render(alpha);
    this.horizon.render();
    this.body.scale.set(this.body.scale.x * this.horizon.pulse);
  }

  override environmentalForce(px: number, py: number): { x: number; y: number } | null {
    if (this.dead) return null;
    const dx = this.x - px;
    const dy = this.y - py;
    const d = Math.hypot(dx, dy);
    if (d < this.def.radius + 14 || d > RANGE) return null;
    const base = this.hpPct <= 0.3 ? ENRAGE_PULL : PULL;
    const strength = base * (1 - d / RANGE);
    return { x: (dx / d) * strength, y: (dy / d) * strength };
  }
}
